// src/pages/HomeDecorProducts.js
import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { 
  Clock, 
  Star, 
  Heart, 
  Eye, 
  ShoppingCart, 
  Gift,
  Home,
  Coffee,
  Timer,
  Palette,
  Search
} from 'lucide-react';
const HomeDecorProducts = () => {
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [sortBy, setSortBy] = useState('name');
  const [searchTerm, setSearchTerm] = useState('');
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1
  });

  const homeDecorProducts = [
    // Clocks
    {
      id: 101,
      name: "Ocean Wave Resin Clock",
      category: "clock",
      price: 2800,
      originalPrice: 3200,
      image: "https://images.unsplash.com/photo-1563861826100-9cb868fdbe1c?w=600&h=600&fit=crop",
      images: [
        "https://images.unsplash.com/photo-1563861826100-9cb868fdbe1c?w=600&h=600&fit=crop",
        "https://images.unsplash.com/photo-1595777457583-95e059d581b8?w=600&h=600&fit=crop"
      ],
      description: "Stunning wall clock featuring mesmerizing ocean wave patterns in blue and white resin with silent quartz movement.",
      features: ["Silent Quartz Movement", "Ocean Wave Design", "UV Resistant", "12-inch Diameter", "Battery Operated"],
      rating: 4.8,
      reviews: 24,
      inStock: true,
      featured: true,
      type: "Wall Clock",
      colors: ["Ocean Blue", "Sea Green", "Coral Pink"]
    },
    {
      id: 102,
      name: "Wooden Resin Galaxy Clock",
      category: "clock",
      price: 3500,
      originalPrice: 4000,
      image: "https://images.unsplash.com/photo-1595777457583-95e059d581b8?w=600&h=600&fit=crop",
      images: [
        "https://images.unsplash.com/photo-1595777457583-95e059d581b8?w=600&h=600&fit=crop",
        "https://images.unsplash.com/photo-1563861826100-9cb868fdbe1c?w=600&h=600&fit=crop"
      ],
      description: "Handcrafted wooden clock with galaxy-inspired resin center, featuring LED backlighting for a stunning night-time display.",
      features: ["LED Backlighting", "Premium Wood Base", "Galaxy Resin Center", "14-inch Diameter", "Remote Control"],
      rating: 4.9,
      reviews: 18,
      inStock: true,
      featured: true,
      type: "LED Clock",
      colors: ["Purple Galaxy", "Blue Nebula", "Green Cosmos"]
    },
    {
      id: 103,
      name: "Minimalist Epoxy Desk Clock",
      category: "clock",
      price: 1800,
      originalPrice: 2200,
      image: "https://images.unsplash.com/photo-1611532736597-de2d4265fba3?w=600&h=600&fit=crop",
      images: [
        "https://images.unsplash.com/photo-1611532736597-de2d4265fba3?w=600&h=600&fit=crop"
      ],
      description: "Clean, modern desk clock with clear epoxy resin and embedded natural elements for a sophisticated workspace accent.",
      features: ["Desktop Size", "Clear Epoxy", "Natural Elements", "Silent Movement", "Modern Design"],
      rating: 4.7,
      reviews: 31,
      inStock: true,
      featured: false,
      type: "Desk Clock",
      colors: ["Clear", "Amber", "Smoke"]
    },

    // Trays
    {
      id: 201,
      name: "Handmade Resin Serving Tray",
      category: "tray",
      price: 2200,
      originalPrice: 2800,
      image: "https://images.unsplash.com/photo-1578662996442-48f60103fc96?w=600&h=600&fit=crop",
      images: [
        "https://images.unsplash.com/photo-1578662996442-48f60103fc96?w=600&h=600&fit=crop",
        "https://images.unsplash.com/photo-1586023492125-27b2c045efd7?w=600&h=600&fit=crop"
      ],
      description: "Elegant serving tray with flowing resin patterns, perfect for entertaining guests or as a decorative accent piece.",
      features: ["Food Safe Surface", "Easy Clean", "Anti-Slip Base", "16x12 inches", "Handcrafted"],
      rating: 4.6,
      reviews: 42,
      inStock: true,
      featured: true,
      type: "Serving Tray",
      colors: ["Marble White", "Ocean Blue", "Forest Green"]
    },
    {
      id: 202,
      name: "Epoxy Resin Jewelry Tray",
      category: "tray",
      price: 1500,
      originalPrice: 1800,
      image: "https://images.unsplash.com/photo-1452860606245-08befc0ff44b?w=600&h=600&fit=crop",
      images: [
        "https://images.unsplash.com/photo-1452860606245-08befc0ff44b?w=600&h=600&fit=crop"
      ],
      description: "Compact jewelry tray with compartments, featuring beautiful resin art patterns to organize your precious accessories.",
      features: ["Multiple Compartments", "Velvet Bottom", "Compact Size", "8x6 inches", "Gift Box Included"],
      rating: 4.8,
      reviews: 28,
      inStock: true,
      featured: false,
      type: "Jewelry Tray",
      colors: ["Rose Gold", "Silver", "Gold"]
    },
    {
      id: 203,
      name: "Large Resin Display Tray",
      category: "tray",
      price: 3200,
      originalPrice: 3800,
      image: "https://images.unsplash.com/photo-1513475382585-d06e58bcb0e0?w=600&h=600&fit=crop",
      images: [
        "https://images.unsplash.com/photo-1513475382585-d06e58bcb0e0?w=600&h=600&fit=crop"
      ],
      description: "Large decorative tray perfect for coffee tables, featuring intricate resin artwork and premium finishing.",
      features: ["Large Size", "Premium Finish", "Decorative Edges", "20x14 inches", "Statement Piece"],
      rating: 4.7,
      reviews: 15,
      inStock: true,
      featured: true,
      type: "Display Tray",
      colors: ["Midnight Black", "Pearl White", "Copper"]
    },

    // Coasters
    {
      id: 301,
      name: "Wooden Resin Coaster Set",
      category: "coaster",
      price: 1200,
      originalPrice: 1500,
      image: "https://images.unsplash.com/photo-1588196749597-9ff075ee6b5b?w=600&h=600&fit=crop",
      images: [
        "https://images.unsplash.com/photo-1588196749597-9ff075ee6b5b?w=600&h=600&fit=crop",
        "https://images.unsplash.com/photo-1541961017774-22349e4a1262?w=600&h=600&fit=crop"
      ],
      description: "Set of 4 wooden coasters with resin river designs, each one unique with natural wood grain patterns.",
      features: ["Set of 4", "Natural Wood", "Resin River", "Cork Backing", "Gift Box"],
      rating: 4.9,
      reviews: 67,
      inStock: true,
      featured: true,
      type: "Coaster Set",
      colors: ["Walnut", "Oak", "Cherry"]
    },
    {
      id: 302,
      name: "Agate Inspired Resin Coasters",
      category: "coaster",
      price: 1800,
      originalPrice: 2200,
      image: "https://images.unsplash.com/photo-1541961017774-22349e4a1262?w=600&h=600&fit=crop",
      images: [
        "https://images.unsplash.com/photo-1541961017774-22349e4a1262?w=600&h=600&fit=crop"
      ],
      description: "Beautiful agate-inspired coasters with natural stone patterns recreated in premium resin with gold accents.",
      features: ["Agate Pattern", "Gold Accents", "Set of 6", "Heat Resistant", "Luxury Finish"],
      rating: 4.8,
      reviews: 33,
      inStock: true,
      featured: false,
      type: "Luxury Coasters",
      colors: ["Blue Agate", "Purple Agate", "Green Agate"]
    },
    {
      id: 303,
      name: "Geode Resin Coaster Collection",
      category: "coaster",
      price: 2500,
      originalPrice: 3000,
      image: "https://images.unsplash.com/photo-1578662996442-48f60103fc96?w=600&h=600&fit=crop",
      images: [
        "https://images.unsplash.com/photo-1578662996442-48f60103fc96?w=600&h=600&fit=crop"
      ],
      description: "Premium geode-inspired coasters featuring crystalline patterns and metallic accents for an elegant table setting.",
      features: ["Geode Design", "Metallic Accents", "Set of 8", "Premium Quality", "Collector's Edition"],
      rating: 4.9,
      reviews: 21,
      inStock: true,
      featured: true,
      type: "Collector's Set",
      colors: ["Amethyst", "Quartz", "Emerald"]
    }
  ];

  const categories = [
    { id: 'all', name: 'All Products', icon: Home, count: homeDecorProducts.length },
    { id: 'clock', name: 'Resin Clocks', icon: Clock, count: homeDecorProducts.filter(p => p.category === 'clock').length },
    { id: 'tray', name: 'Resin Trays', icon: Gift, count: homeDecorProducts.filter(p => p.category === 'tray').length },
    { id: 'coaster', name: 'Resin Coasters', icon: Coffee, count: homeDecorProducts.filter(p => p.category === 'coaster').length }
  ];

  const sortOptions = [
    { value: 'name', label: 'Name A-Z' },
    { value: 'price-low', label: 'Price: Low to High' },
    { value: 'price-high', label: 'Price: High to Low' },
    { value: 'rating', label: 'Highest Rated' }
  ];

  const filteredProducts = homeDecorProducts.filter(product => {
    const matchesCategory = selectedCategory === 'all' || product.category === selectedCategory;
    const matchesSearch = product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         product.description.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesCategory && matchesSearch;
  }).sort((a, b) => {
    switch (sortBy) {
      case 'price-low':
        return a.price - b.price;
      case 'price-high':
        return b.price - a.price;
      case 'rating':
        return b.rating - a.rating;
      case 'name':
      default:
        return a.name.localeCompare(b.name);
    }
  });

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6
      }
    }
  };

  return (
    <div className="min-h-screen bg-dark-950 pt-20">
      {/* Hero Section */}
      <section className="py-16 bg-gradient-to-br from-dark-900 to-dark-950 relative overflow-hidden">
        <div className="container mx-auto px-4 max-w-6xl">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <h1 className="text-4xl md:text-6xl font-bold font-serif text-white mb-6">
              Home <span className="bg-gradient-to-r from-accent-400 to-accent-600 bg-clip-text text-transparent">Decor</span>
            </h1>
            <p className="text-xl text-dark-200 max-w-3xl mx-auto leading-relaxed">
              Transform your living space with our exquisite collection of resin clocks, trays, and coasters. 
              Each piece is a perfect blend of functionality and artistry.
            </p>
          </motion.div>
        </div>
        
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-accent-500/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 right-1/4 w-64 h-64 bg-accent-600/10 rounded-full blur-3xl"></div>
      </section>

      {/* Category Navigation */}
      <section className="py-12 bg-dark-800">
        <div className="container mx-auto px-4 max-w-6xl">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {categories.map((category) => (
              <motion.button
                key={category.id}
                onClick={() => setSelectedCategory(category.id)}
                className={`p-6 rounded-2xl text-center transition-all duration-300 ${
                  selectedCategory === category.id
                    ? 'bg-gradient-to-br from-accent-500 to-accent-600 text-white shadow-lg scale-105'
                    : 'bg-dark-700/50 text-dark-300 hover:bg-dark-700 hover:text-white'
                }`}
                whileHover={{ scale: selectedCategory === category.id ? 1.05 : 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <category.icon className="h-12 w-12 mx-auto mb-4" />
                <h3 className="text-lg font-semibold mb-2">{category.name}</h3>
                <p className="text-sm opacity-90">{category.count} Products</p>
              </motion.button>
            ))}
          </div>
        </div>
      </section>

      {/* Products Section */}
      <section className="py-16">
        <div className="container mx-auto px-4 max-w-6xl">
          <motion.div
            ref={ref}
            initial="hidden"
            animate={inView ? "visible" : "hidden"}
            variants={containerVariants}
          >
            {/* Filters and Search */}
            <motion.div variants={itemVariants} className="mb-12">
              <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center space-y-6 lg:space-y-0">
                {/* Search */}
                <div className="relative flex-1 max-w-md">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-dark-400" />
                  <input
                    type="text"
                    placeholder="Search products..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full bg-dark-800 border border-dark-600 rounded-lg pl-10 pr-4 py-3 text-white placeholder-dark-400 focus:border-accent-500 focus:outline-none"
                  />
                </div>

                {/* Sort and Results */}
                <div className="flex flex-col sm:flex-row items-start sm:items-center space-y-4 sm:space-y-0 sm:space-x-6">
                  <div className="text-dark-300">
                    Showing <span className="text-white font-semibold">{filteredProducts.length}</span> products
                  </div>
                  
                  <div className="flex items-center space-x-3">
                    <label className="text-dark-300 text-sm font-medium">Sort by:</label>
                    <select
                      value={sortBy}
                      onChange={(e) => setSortBy(e.target.value)}
                      className="bg-dark-800 border border-dark-600 text-white px-4 py-2 rounded-lg focus:border-accent-500 focus:outline-none"
                    >
                      {sortOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Products Grid */}
            <motion.div variants={containerVariants} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredProducts.map((product, index) => (
                <motion.div
                  key={product.id}
                  variants={itemVariants}
                  className="group cursor-pointer"
                >
                  <div className="bg-dark-800/50 backdrop-blur-md rounded-2xl overflow-hidden border border-dark-700 hover:border-accent-500/30 transition-all duration-500 hover:scale-105">
                    {/* Product Image */}
                    <div className="relative aspect-square overflow-hidden">
                      <img
                        src={product.image}
                        alt={product.name}
                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                      />
                      
                      {/* Overlay */}
                      <div className="absolute inset-0 bg-black/20 group-hover:bg-black/40 transition-colors duration-300"></div>
                      
                      {/* Floating Actions */}
                      <div className="absolute top-4 right-4 flex flex-col space-y-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                        <button className="w-10 h-10 bg-white/20 backdrop-blur-md rounded-full flex items-center justify-center hover:bg-white/30 transition-colors duration-300">
                          <Heart className="h-4 w-4 text-white" />
                        </button>
                        <button className="w-10 h-10 bg-white/20 backdrop-blur-md rounded-full flex items-center justify-center hover:bg-white/30 transition-colors duration-300">
                          <Eye className="h-4 w-4 text-white" />
                        </button>
                      </div>

                      {/* Sale Badge */}
                      {product.originalPrice > product.price && (
                        <div className="absolute top-4 left-4">
                          <span className="bg-accent-500 text-white px-3 py-1 rounded-full text-sm font-medium">
                            Sale
                          </span>
                        </div>
                      )}

                      {/* Featured Badge */}
                      {product.featured && (
                        <div className="absolute top-4 left-4">
                          <span className="bg-yellow-500 text-white px-3 py-1 rounded-full text-sm font-medium">
                            Featured
                          </span>
                        </div>
                      )}

                      {/* Product Type */}
                      <div className="absolute bottom-4 left-4 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                        <span className="bg-black/50 backdrop-blur-md text-white px-3 py-1 rounded-lg text-sm">
                          {product.type}
                        </span>
                      </div>
                    </div>

                    {/* Product Info */}
                    <div className="p-6">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-accent-400 text-sm font-medium uppercase tracking-wide">
                          {product.category}
                        </span>
                        <div className="flex items-center space-x-1">
                          <Star className="h-4 w-4 text-yellow-400 fill-current" />
                          <span className="text-sm text-dark-300">{product.rating}</span>
                        </div>
                      </div>
                      
                      <h3 className="text-xl font-semibold text-white mb-2 group-hover:text-accent-400 transition-colors duration-300 line-clamp-2">
                        {product.name}
                      </h3>
                      
                      <p className="text-dark-300 text-sm mb-4 leading-relaxed line-clamp-2">
                        {product.description}
                      </p>

                      {/* Features */}
                      <div className="mb-4">
                        <div className="flex flex-wrap gap-2">
                          {product.features.slice(0, 2).map((feature, featureIndex) => (
                            <span
                              key={featureIndex}
                              className="text-xs bg-dark-700 text-dark-300 px-2 py-1 rounded-full"
                            >
                              {feature}
                            </span>
                          ))}
                          {product.features.length > 2 && (
                            <span className="text-xs text-accent-400">
                              +{product.features.length - 2} more
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Colors */}
                      <div className="mb-4">
                        <div className="flex items-center space-x-2">
                          <Palette className="h-4 w-4 text-dark-400" />
                          <span className="text-xs text-dark-400">
                            {product.colors.length} color{product.colors.length > 1 ? 's' : ''} available
                          </span>
                        </div>
                      </div>

                      {/* Price and Actions */}
                      <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center space-x-2">
                          <span className="text-xl font-bold text-white">
                            ₹{product.price.toLocaleString()}
                          </span>
                          {product.originalPrice > product.price && (
                            <span className="text-dark-400 text-sm line-through">
                              ₹{product.originalPrice.toLocaleString()}
                            </span>
                          )}
                        </div>
                        <div className="text-green-400 text-sm font-medium">
                          In Stock
                        </div>
                      </div>

                      {/* Action Buttons */}
                      <div className="grid grid-cols-2 gap-3">
                        <button className="bg-gradient-to-r from-accent-500 to-accent-600 text-white py-2 px-4 rounded-lg font-medium hover:from-accent-600 hover:to-accent-700 transition-all duration-300 flex items-center justify-center space-x-2">
                          <ShoppingCart className="h-4 w-4" />
                          <span>Add to Cart</span>
                        </button>
                        <button className="border border-accent-500 text-accent-400 py-2 px-4 rounded-lg font-medium hover:bg-accent-500/10 transition-all duration-300">
                          View Details
                        </button>
                      </div>

                      {/* Reviews */}
                      <div className="mt-4 pt-4 border-t border-dark-700">
                        <div className="text-xs text-dark-400">
                          {product.reviews} customer review{product.reviews !== 1 ? 's' : ''}
                        </div>
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </motion.div>

            {/* No Results */}
            {filteredProducts.length === 0 && (
              <motion.div
                variants={itemVariants}
                className="text-center py-16"
              >
                <Home className="h-16 w-16 text-dark-600 mx-auto mb-4" />
                <h3 className="text-2xl font-semibold text-white mb-4">
                  No products found
                </h3>
                <p className="text-dark-300 mb-6">
                  Try adjusting your search or filter criteria.
                </p>
                <button
                  onClick={() => {
                    setSelectedCategory('all');
                    setSearchTerm('');
                  }}
                  className="bg-gradient-to-r from-accent-500 to-accent-600 text-white py-3 px-6 rounded-lg font-medium hover:from-accent-600 hover:to-accent-700 transition-all duration-300"
                >
                  View All Products
                </button>
              </motion.div>
            )}
          </motion.div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 bg-dark-800">
        <div className="container mx-auto px-4 max-w-6xl">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl font-bold text-white mb-6">
              Why Choose Our <span className="bg-gradient-to-r from-accent-400 to-accent-600 bg-clip-text text-transparent">Home Decor</span>
            </h2>
            <p className="text-dark-300 max-w-2xl mx-auto">
              Every piece in our collection is crafted with precision and passion to bring beauty and functionality to your home.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                icon: Timer,
                title: "Long-lasting Quality",
                description: "Built to last with premium materials and expert craftsmanship"
              },
              {
                icon: Palette,
                title: "Unique Designs",
                description: "Each piece is one-of-a-kind with artistic resin patterns"
              },
              {
                icon: Gift,
                title: "Perfect Gifts",
                description: "Beautifully packaged and ready for any special occasion"
              }
            ].map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.2 }}
                className="text-center"
              >
                <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-accent-500 to-accent-600 rounded-2xl mb-6">
                  <feature.icon className="h-8 w-8 text-white" />
                </div>
                <h3 className="text-xl font-semibold text-white mb-3">
                  {feature.title}
                </h3>
                <p className="text-dark-300 leading-relaxed">
                  {feature.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16">
        <div className="container mx-auto px-4 max-w-4xl">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="bg-gradient-to-br from-accent-500/20 to-accent-600/20 rounded-3xl p-12 text-center border border-accent-500/30"
          >
            <Home className="h-16 w-16 text-accent-400 mx-auto mb-6" />
            <h2 className="text-3xl font-bold text-white mb-6">
              Transform Your Space Today
            </h2>
            <p className="text-xl text-dark-200 mb-8 max-w-2xl mx-auto">
              Ready to add some artistic flair to your home? Browse our complete collection or get a custom piece made just for you.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button className="bg-gradient-to-r from-accent-500 to-accent-600 text-white py-4 px-8 rounded-lg font-medium hover:from-accent-600 hover:to-accent-700 transition-all duration-300">
                Shop Collection
              </button>
              <button className="border border-accent-500 text-accent-400 py-4 px-8 rounded-lg font-medium hover:bg-accent-500/10 transition-all duration-300">
                Custom Order
              </button>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default HomeDecorProducts;